import { useEffect } from 'react';
import { string as zString } from 'zod';
import { init as initBraze } from '@hungryroot/braze';
import { useGoogleAnalytics } from '@hungryroot/google-analytics';
import { setUser, setExperiments, logExceptionOnce } from '@hungryroot/o11y';
import { StatusCode, type Customer } from '@hungryroot/rest';
import { useCustomerExperiments } from './useCustomerExperiments';
import { useCustomerSelf } from './useCustomerSelf';

export enum AuthState {
  isError,
  isLoading,
  isAuthenticated,
  isAnonymous,
}

export interface AuthStateAuthenticated {
  authState: AuthState.isAuthenticated;
  customerData: Customer;
}

export interface AuthStateAnonymous {
  authState: AuthState.isAnonymous;
  customerData?: never;
}

export interface AuthStateError {
  authState: AuthState.isError;
  customerData?: never;
}

export interface AuthStateLoading {
  authState: AuthState.isLoading;
  customerData?: Customer;
}

export type UseCustomerResponse =
  | AuthStateAuthenticated
  | AuthStateAnonymous
  | AuthStateError
  | AuthStateLoading;

const PUBLIC_BRAZE_SDK_API_KEY = zString({
  required_error:
    'Environment variable "PUBLIC_BRAZE_SDK_API_KEY" not defined!',
}).parse(process.env.PUBLIC_BRAZE_SDK_API_KEY);

const PUBLIC_BRAZE_SDK_ENDPOINT = zString({
  required_error:
    'Environment variable "PUBLIC_BRAZE_SDK_ENDPOINT" not defined!',
}).parse(process.env.PUBLIC_BRAZE_SDK_ENDPOINT);

/**
 * @deprecated This has been replaced by `useIdentity` in packages/customer
 *
 * This hook should only be called once at the root of the application. Use context to access data in components.
 * @returns
 */
export function useCustomer(): UseCustomerResponse {
  const { setGA } = useGoogleAnalytics();
  const auth = useAuthState();
  const { customerData } = auth;

  useEffect(() => {
    if (customerData === undefined) {
      return;
    }

    const customerId = `${customerData.id}`;

    setGA(`userId`, customerId);

    setUser({
      id: customerId,
      name: customerData.first_name || 'undefined',
      email: customerData.email,
    });
  }, [customerData]);

  useEffect(() => {
    if (customerData === undefined) {
      return;
    }

    initBraze({
      apiKey: PUBLIC_BRAZE_SDK_API_KEY,
      baseUrl: PUBLIC_BRAZE_SDK_ENDPOINT,
      userId: customerData.id,
    });
  }, [customerData]);

  const experiments = useCustomerExperiments(
    ['customer-experiments'],
    {
      customerId: customerData?.id === undefined ? 0 : customerData.id,
    },
    {
      enabled: customerData?.id !== undefined,
    }
  );

  useEffect(() => {
    if (experiments.data === undefined) {
      return;
    }

    setExperiments(
      experiments.data.results.map(({ experiment, variant }) => {
        return {
          id: experiment,
          variant,
        };
      })
    );
  }, [experiments.data]);

  return auth;
}

export function useAuthState(): UseCustomerResponse {
  const customerQuery = useCustomerSelf();
  const { data: customerData, isLoading, isError, error } = customerQuery;

  if (isLoading) {
    return {
      authState: AuthState.isLoading,
      customerData,
    };
  }

  if (isError) {
    try {
      const message = error instanceof Error ? JSON.parse(error.message) : null;

      if (message.status === StatusCode.ClientErrorForbidden) {
        return {
          authState: AuthState.isAnonymous,
        };
      }
    } catch (e) {
      logExceptionOnce(
        'Parse error when fetching customer',
        {
          key: 'useAuthState.parseError',
        },
        {
          error: e instanceof Error ? e.message : undefined,
          message: error instanceof Error ? error.message : undefined,
        }
      );
    }

    return {
      authState: AuthState.isError,
    };
  }

  if (customerData !== undefined) {
    return {
      authState: AuthState.isAuthenticated,
      customerData,
    };
  }

  return {
    authState: AuthState.isAnonymous,
  };
}
